import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Mic, Captions, Image, Smartphone, PenLine, Sparkles, Send } from "lucide-react";
import { MaskedLines, FadeIn, Reveal } from "@/components/site/Motion";
import { PriceCard } from "@/components/site/PriceCard";
import { scrollToId } from "@/components/site/SmoothScroll";
import { usePageTitle } from "@/hooks/useMimis";
import { VideoCreator } from "@/components/video/VideoCreator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const DESCRIPTION =
  "Scrivi un'idea in italiano e in circa 60 secondi ricevi un video con voce italiana, sottotitoli e immagini, pronto per TikTok e Instagram.";

const FEATURES = [
  { icon: Mic, title: "Voce umana italiana", text: "Una voce naturale, non robotica, che parla come te." },
  { icon: Captions, title: "Sottotitoli automatici", text: "Parola per parola, a tempo con la voce." },
  { icon: Image, title: "Immagini incluse", text: "Scelte in base alla tua idea, senza cercarle tu." },
  { icon: Smartphone, title: "Formato verticale", text: "Pronto per TikTok, Instagram Reels e Shorts." },
];

const FAQ = [
  { q: "Quanto costa?", a: "Il primo video è gratis. Poi scegli Starter a 9€ al mese (10 video) oppure Pro a 19€ al mese (video illimitati). Disdici quando vuoi." },
  { q: "Cosa ha in più il piano Pro?", a: "Video illimitati e il nome della tua attività nel finale al posto di MIMIS, impostato una volta sola dal tuo account. Le tue 3 foto invece puoi caricarle con qualsiasi piano." },
  { q: "Quanto tempo ci vuole?", a: "Di solito meno di un minuto: script, voce, immagini e sottotitoli vengono creati in automatico." },
  { q: "Devo registrare la mia voce o mostrare la faccia?", a: "No. Scrivi solo l'idea: voce italiana, sottotitoli e immagini li crea MIMIS." },
];

const PromptDemo = () => {
  const full = "3 trucchi per dimagrire";
  const [n, setN] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setN((x) => (x >= full.length + 12 ? 0 : x + 1)), 110);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="rounded-2xl border border-[#e7e7ea] bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#52525b]">La tua idea</p>
      <p className="font-display mt-2 min-h-[2rem] text-xl font-semibold">
        {full.slice(0, n)}<span className="caret ml-0.5 inline-block h-6 w-[2px] translate-y-1 bg-[#0066FF]" />
      </p>
      <span className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-300 ${n >= full.length ? "bg-[#0066FF] text-white" : "bg-[#F5F5F7] text-[#52525b]"}`}>
        <Sparkles className="h-3.5 w-3.5" /> Crea il video
      </span>
    </div>
  );
};

const STEPS = [
  { icon: PenLine, title: "Scrivi l'idea", text: "Una frase in italiano. Basta questo." },
  { icon: Sparkles, title: "MIMIS crea il video", text: "Voce, sottotitoli e immagini in circa 60 secondi." },
  { icon: Send, title: "Pubblica", text: "Scarica e carica su TikTok o Instagram." },
];

export default function ToolVideo() {
  const { hash } = useLocation();
  usePageTitle("MIMIS Video Italia - Crea il tuo video virale in 60 secondi | MIMIS");

  useEffect(() => {
    if (hash !== "#prezzo") return undefined;
    const t = setTimeout(() => scrollToId("prezzo"), 200);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <main data-testid="tool-video-page">
      <section className="relative overflow-hidden pb-24 pt-28 sm:pt-36">
        <div className="bg-grid pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#0066FF]/10 px-3.5 py-1.5 text-xs font-semibold text-[#0066FF] sm:text-sm">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#0066FF]" /> Tool n.1 · Disponibile ora
            </span>
          </FadeIn>
          <h1 className="font-display mt-6 font-extrabold tracking-tight" data-testid="tool-title">
            <MaskedLines delay={0.1} lines={[
              { text: "MIMIS VIDEO ITALIA", className: "text-4xl leading-[0.98] tracking-[-0.04em] sm:text-6xl lg:text-7xl" },
              { text: "Crea il tuo video virale in 60 secondi", className: "mt-2 text-2xl leading-tight text-[#0066FF] sm:text-4xl lg:text-5xl" },
            ]} />
          </h1>
          <FadeIn delay={0.5}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#3f3f46] md:text-lg" data-testid="tool-description">{DESCRIPTION}</p>
          </FadeIn>
          <FadeIn delay={0.65} className="mt-10">
            <VideoCreator />
          </FadeIn>
        </div>
      </section>

      <section className="bg-[#F5F5F7] py-24 sm:py-28" data-testid="tool-steps-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <span className="eyebrow">Da idea a video</span>
            <h2 className="font-display mt-4 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">Una frase.<br /><span className="text-[#a1a1aa]">Un video pronto.</span></h2>
          </Reveal>
          <div className="mt-14 grid gap-4 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <div className="card-soft h-full p-7 sm:p-9">
                <PromptDemo />
                <p className="mt-6 leading-relaxed text-[#52525b]">Scrivi come parli. MIMIS capisce l'idea, prepara il testo, la voce e le immagini.</p>
              </div>
            </Reveal>
            <div className="grid gap-4 lg:col-span-7">
              {STEPS.map(({ icon: Icon, title, text }, i) => (
                <Reveal key={title} delay={i * 0.1}>
                  <div className="card-soft flex items-center gap-5 p-6" data-testid={`tool-step-${i + 1}`}>
                    <span className="font-display w-10 shrink-0 text-3xl font-extrabold tracking-tighter text-[#d4d4d8]">0{i + 1}</span>
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#0066FF] text-white"><Icon className="h-5 w-5" /></span>
                    <div><p className="font-display text-lg font-bold">{title}</p><p className="text-[#52525b]">{text}</p></div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-28" data-testid="tool-features-section">
        <div className="mx-auto grid max-w-7xl gap-4 px-5 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
          {FEATURES.map(({ icon: Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="card-soft group h-full p-7">
                <Icon className="h-7 w-7 text-[#0066FF] transition-transform duration-300 group-hover:scale-110" />
                <p className="font-display mt-6 text-lg font-bold">{title}</p>
                <p className="mt-2 text-[#52525b]">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="prezzo" className="bg-[#F5F5F7] py-24 sm:py-28" data-testid="tool-pricing-section">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <span className="eyebrow">Prezzo</span>
            <h2 className="font-display mt-4 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">Da 9€ al mese.<br /><span className="text-[#0066FF]">Pro a 19€, illimitato.</span></h2>
            <Accordion type="single" collapsible className="mt-10" data-testid="tool-faq">
              {FAQ.map((f, i) => (
                <AccordionItem key={f.q} value={`f${i}`} className="border-[#e2e2e6]">
                  <AccordionTrigger className="text-left font-display text-base font-semibold hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[0.95rem] leading-relaxed text-[#52525b]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.1} className="mx-auto w-full max-w-md">
            <PriceCard />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
